import CopyWrapper from "components/Common/CopyWrapper";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import {
  COLOR_SOURCE_CHANNEL,
  COLOR_USER_TYPE,
  ENUM_SOURCE_CHANNEL,
  LIST_CHANNEL_USER_OPTION,
  LIST_OPTION_OS,
  LIST_OPTION_SOURCE_CHANNEL,
  LIST_OPTION_USER_TYPE,
} from "helpers/constans";
import { formatDateStringToKorea, getPlatFormImage } from "helpers/format";
import IPHONE_MODELS from "helpers/iphone_models";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import { capitalizeFirstLetter } from "utils";
import NO_IMAGE_USER from "../../../../assets/images/no-user.png";
import countries from "helpers/countries";
import { JobSeekerProfileFormat } from "api/types/_user";
import { putUser } from "store/thunks";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { toast } from "react-toastify";

registerLocale("en", en);
registerLocale("ko", ko);

const AccountInfoTab = ({ data, handleUpdateSuccess, setLinkImage }: any) => {
  const { t, i18n } = useTranslation();
  const [dataFormat, setDataFormat] = useState<JobSeekerProfileFormat | null>(null);

  const handleUpdateStatus = async (type: "isVisibleInRecruitment" | "isActive", value: boolean) => {
    try {
      if (!data?.id) return;

      const response: any = await putUser(data?.id, {
        account: {
          [type]: value
        },
      });

      if (response?.data) {
        handleUpdateSuccess && handleUpdateSuccess();

        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  }

  useEffect(() => {
    if (data) {
      const platForm = data?.provider;
      const deviceOS = data?.userDevice.device.os;
      const appVersion = data?.userDevice?.appVersion;
      const appBuild = data?.userDevice?.appBuild;
      const formatAppVersion = appBuild ? `${appVersion} (${appBuild})` : appVersion;
      let deviceModel = data?.userDevice?.device?.model;
      if (deviceOS === "ios") {
        deviceModel = deviceModel
          ? IPHONE_MODELS[deviceModel as keyof typeof IPHONE_MODELS] ?? deviceModel
          : "";
      }
      const countryCode = data?.userDevice?.ipAddressCountryCode;
      const country = countries.find((item) => item?.code === countryCode);

      setDataFormat({
        userID: data?.id,
        imageUrls: data?.store?.thumbnailUrl || "",
        isStoreBusinessRegistered: data?.isStoreBusinessRegistered,
        isStoreLinked: data?.isStoreLinked,
        isVisibleInRecruitment: data?.isVisibleInRecruitment,
        platformName: platForm ? platForm?.toLowerCase() : "",
        platformImg: platForm ? getPlatFormImage(platForm) : "",
        deviceOS: deviceOS || "",
        deviceOSImg: deviceOS ? getPlatFormImage(deviceOS) : "",
        isActive: data?.isActive,
        emailSNS: data?.email ? data?.email : data?.providerId,
        udid: data?.userDevice?.device?.udid,
        appVersion: formatAppVersion,
        channel: data?.installSource || "",
        channelImg: data?.installSource ? getPlatFormImage(data?.installSource) : "",
        userType: data?.userDevice?.loginType === "GUEST" ? "GUEST" : "NORMAL",
        sourceChannel: data?.sourceChannel || ENUM_SOURCE_CHANNEL.APP,
        deviceModel: deviceModel,
        deviceManufacturer: data?.userDevice?.device?.manufacturer,
        ipAddress: data?.userDevice?.ipAddress,
        lat: data?.address?.lat,
        long: data?.address?.long,
        address: data?.userDevice?.address,
        shortAddress: data?.userDevice?.shortAddress,
        country: country
      })
    }
  }, [JSON.stringify(data)])

  useEffect(() => {
    document.title = `${t("Account Info")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Row>
        <Col lg={12} className="mt-4 px-4 detail-section">
          <div>
            <div
              className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
              style={{
                border: "1px dashed var(--vz-primary)",
                borderRadius: "4px",
              }}
            >
              <span
                className="text-primary position-absolute fs-13 px-1"
                style={{
                  backgroundColor: "var(--vz-secondary-bg)",
                  top: "0px",
                  transform: "translateY(-50%)",
                }}
              >
                {t("Info")}
              </span>
              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div className="mb-3">
                  <Label htmlFor="contact_name" className="form-label">
                    {t("Store Photo")}
                  </Label>

                  <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-5">
                    <div
                      className="position-relative"
                      style={{
                        width: "160px",
                        height: "160px",
                        border: "1px solid var(--vz-input-border-custom)",
                        overflow: "hidden",
                        borderRadius: "var(--vz-border-radius)",
                      }}
                    >
                      <img
                        className="cursor-pointer"
                        src={dataFormat?.imageUrls || NO_IMAGE_USER}
                        alt={`preview-user`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null; // prevents looping
                          currentTarget.src = NO_IMAGE_USER;
                        }}
                        onClick={() => {
                          setLinkImage && setLinkImage({ url: dataFormat?.imageUrls });
                        }}
                      />
                    </div>

                    <div className="pt-2">
                      {
                        !dataFormat?.isStoreLinked && (
                          <div className="d-flex gap-2 align-items-center">
                            <span style={{ fontSize: 18 }}>⚠️</span>
                            {t("No registered store information found.")}
                          </div>
                        )
                      }

                      {
                        !dataFormat?.isStoreBusinessRegistered && (
                          <div className="d-flex gap-2 align-items-center">
                            <span style={{ fontSize: 18 }}>⚠️</span>
                            {t("No registered business owner information found.")}
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div className="flex-grow-1">
                  <Row>
                    <Col md={12} lg={12}>
                      <Card>
                        <CardBody>
                          <Row>
                            <Col md={3} lg={3}>
                              <div className="mb-3">
                                <Label htmlFor="name" className="form-label">
                                  {t("User ID")}
                                </Label>
                                <div className="cursor-pointer">
                                  {data?.id ? (
                                    <CopyWrapper contentCopy={data?.id}>
                                      {data?.id}
                                    </CopyWrapper>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </div>
                            </Col>
                            <Col md={3} lg={3}>
                              <div className="mb-3">
                                <Label
                                  htmlFor="platform"
                                  className="form-label"
                                >
                                  {t("Platform")}
                                </Label>

                                <div className="d-flex flex-column gap-1">
                                  {dataFormat?.platformName ? (
                                    <div className="cursor-pointer d-flex gap-2 align-items-center whitespace-nowrap">
                                      <div style={{ width: "25px" }}>
                                        {dataFormat?.platformImg && (
                                          <img
                                            src={dataFormat?.platformImg}
                                            width={25}
                                            height={25}
                                            alt="userProvider"
                                          />
                                        )}
                                      </div>
                                      <div className="cursor-pointer">
                                        {t(
                                          capitalizeFirstLetter(
                                            dataFormat?.platformName === "default" ||
                                              dataFormat?.platformName == "guest"
                                              ? "Email"
                                              : dataFormat?.platformName
                                          )
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3">
                                <Label htmlFor="address" className="form-label">
                                  {t("OS")}
                                </Label>

                                <div className="d-flex align-items-center">
                                  {dataFormat?.deviceOS ? (
                                    <>
                                      <div
                                        style={{ width: "25px" }}
                                        className="me-1"
                                      >
                                        {dataFormat?.deviceOSImg ? (
                                          <img
                                            src={dataFormat?.deviceOSImg}
                                            width={20}
                                            height={20}
                                            alt="platform"
                                          />
                                        ) : (
                                          <span
                                            className={`bg-secondary`}
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                              borderRadius: "3px",
                                            }}
                                          ></span>
                                        )}
                                      </div>
                                      <div>
                                        {t(
                                          LIST_OPTION_OS?.find(
                                            (item) => item?.value === dataFormat?.deviceOS
                                          )?.label ?? ""
                                        )}
                                      </div>
                                    </>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="d-flex align-items-end">
                                <div className="form-check form-switch mb-0 mt-0 mb-3">
                                  <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="isActive"
                                    name="isActive"
                                    checked={!!dataFormat?.isActive}
                                    onChange={(event: any) => {
                                      event.preventDefault();
                                      handleUpdateStatus("isActive", event?.target?.checked)
                                      console.log("isActive");
                                    }}
                                    style={{ cursor: "default" }}
                                  />

                                  <Label
                                    className="form-check-label"
                                    style={{ cursor: "default" }}
                                    for="is_active"
                                  >
                                    {t("Label Active")}
                                  </Label>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="website" className="form-label">
                                  {t("Email SNS")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper contentCopy={dataFormat?.emailSNS}>
                                    {dataFormat?.emailSNS || "-"}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="website" className="form-label">
                                  {t("UUID")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper
                                    contentCopy={dataFormat?.udid}
                                  >
                                    {dataFormat?.udid || "-"}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="website" className="form-label">
                                  {t("App Version")}{" "}
                                </Label>
                                <div>
                                  {dataFormat?.appVersion ?? "-"}
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="channel" className="form-label">
                                  {t("Channel")}
                                </Label>
                                <div className="d-flex flex-column gap-1">
                                  {dataFormat?.channel ? (
                                    <div className="cursor-pointer d-flex gap-2 align-items-center whitespace-nowrap">
                                      <div style={{ width: "25px" }}>
                                        {dataFormat?.channelImg && (
                                          <img
                                            src={dataFormat?.channelImg}
                                            width={25}
                                            height={25}
                                            alt="channel"
                                          />
                                        )}
                                      </div>
                                      <div className="cursor-pointer">
                                        {t(
                                          LIST_CHANNEL_USER_OPTION?.find(
                                            (item: any) =>
                                              String(item?.value) ===
                                              String(dataFormat?.channel)
                                          )?.label || ""
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="field" className="form-label">
                                  {t("User Type")}{" "}
                                </Label>
                                <div>
                                  <span
                                    className={`rounded-pill badge bg-${COLOR_USER_TYPE[dataFormat?.userType] || "secondary"
                                      }`}
                                  >
                                    {t(
                                      LIST_OPTION_USER_TYPE?.find(
                                        (item: any) =>
                                          String(item?.value) ===
                                          String(dataFormat?.userType)
                                      )?.label || ""
                                    )}
                                  </span>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="field" className="form-label">
                                  {t("Source Channel")}{" "}
                                </Label>
                                <div>
                                  <span
                                    className={`rounded-pill badge bg-${COLOR_SOURCE_CHANNEL[dataFormat?.sourceChannel || ENUM_SOURCE_CHANNEL.APP] || "secondary"
                                      }`}
                                  >
                                    {t(
                                      LIST_OPTION_SOURCE_CHANNEL?.find(
                                        (item: any) =>
                                          String(item?.value) ===
                                          String(dataFormat?.sourceChannel)
                                      )?.label || ""
                                    )}
                                  </span>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="address" className="form-label">
                                  {t("Device Model")}
                                </Label>
                                <div>{dataFormat?.deviceModel || "-"}</div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label
                                  htmlFor="manufacturer"
                                  className="form-label"
                                >
                                  {t("Device Manufacturer")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper
                                    contentCopy={dataFormat?.deviceManufacturer}
                                  >
                                    {dataFormat?.deviceManufacturer ||
                                      "-"}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            {/* <Col md={3} lg={3}></Col> */}

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="ip" className="form-label">
                                  {t("Ip Address")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper
                                    contentCopy={dataFormat?.ipAddress}
                                  >
                                    {`${dataFormat?.ipAddress || "-"}`}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="lat" className="form-label">
                                  {t("Lat")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper contentCopy={dataFormat?.lat}>
                                    {`${dataFormat?.lat || "-"}`}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="long" className="form-label">
                                  {t("Long")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper
                                    contentCopy={dataFormat?.long}
                                  >
                                    {`${dataFormat?.long || "-"}`}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="long" className="form-label">
                                  {t("Country")}
                                </Label>
                                <div className="cursor-pointer">
                                  {dataFormat?.country ? (
                                    <div className="d-flex flex-column gap-1">
                                      {
                                        <div className="cursor-pointer d-flex gap-2 align-items-center ">
                                          <img
                                            src={dataFormat?.country?.flags}
                                            width={20}
                                            height={20}
                                            alt="profile"
                                          />
                                          <div className="cursor-pointer">
                                            {i18n?.language === "ko"
                                              ? dataFormat?.country?.name_korea
                                              : dataFormat?.country?.name}
                                          </div>
                                        </div>
                                      }
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </div>
                            </Col>

                            <Col md={12} lg={12}>
                              <div className="mb-3 mt-2">
                                <Label htmlFor="address" className="form-label">
                                  {t("Address")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper
                                    contentCopy={dataFormat?.address}
                                  >
                                    {`${dataFormat?.address || "-"}`}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={12} lg={12}>
                              <div className="mb-3 mt-2">
                                <Label
                                  htmlFor="short_address"
                                  className="form-label"
                                >
                                  {t("Short Address")}
                                </Label>
                                <div className="cursor-pointer">
                                  <CopyWrapper
                                    contentCopy={dataFormat?.shortAddress}
                                  >
                                    {`${dataFormat?.shortAddress || "-"}`}
                                  </CopyWrapper>
                                </div>
                              </div>
                            </Col>

                            <Col md={3} lg={3}>
                              <div className="mb-3 mt-2">
                                <Label
                                  htmlFor="signup_date"
                                  className="form-label"
                                >
                                  {t("Signup Date")}
                                </Label>
                                <div>
                                  {data?.registeredAt
                                    ? formatDateStringToKorea(
                                      data?.registeredAt
                                    )
                                    : "-"}
                                </div>
                              </div>
                            </Col>

                            {/* <Col md={3} lg={3}>
                              <div className="mb-3">
                                <Label
                                  htmlFor="last_activity_time"
                                  className="form-label"
                                >
                                  {t("Last Activity Time")}
                                </Label>
                                <div>
                                  {data?.last_activity_time
                                    ? formatDateStringToKorea(
                                        data?.last_activity_time
                                      )
                                    : "-"}
                                </div>
                              </div>
                            </Col> */}
                          </Row>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </div>

              <Row className="mt-3"></Row>
            </div>
            <div></div>
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default AccountInfoTab;
